import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { useAuth } from '../context/AuthContext';

const STORAGE_KEY = 'jtalk-oauth-pending';
const native = Capacitor.isNativePlatform();
const storage = () => native ? localStorage : sessionStorage;
const base64url = bytes => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
const RETURN_URI = 'jtalk://oauth/callback';

export function useSocialLogin() {
  const { completeSocialLogin } = useAuth();
  const [providers, setProviders] = useState([]);
  const [providersLoaded, setProvidersLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [linkRequest, setLinkRequest] = useState(null);
  const handling = useRef(false);
  const starting = useRef(false);

  useEffect(() => {
    let active = true;
    let loading = false;
    const refresh = async () => {
      if (loading || document.visibilityState === 'hidden') return;
      loading = true;
      try {
        const { data } = await axios.get('/api/auth/oauth/providers', { timeout: 10000 });
        if (active) { setProviders(data.providers || []); setProvidersLoaded(true); }
      } catch {
        // Keep the last known status during a server restart; retry on focus or the next interval.
      } finally { loading = false; }
    };
    refresh();
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);

  const receive = useCallback(async (returnUrl) => {
    let url;
    try { url = new URL(returnUrl); } catch { return; }
    if (native ? `${url.protocol}//${url.host}${url.pathname}` !== RETURN_URI : (url.origin !== window.location.origin || url.pathname !== '/login')) return;
    const params = new URLSearchParams(url.hash.slice(1));
    if (!params.has('oauth_code') && !params.has('oauth_error')) return;
    if (handling.current) return;
    handling.current = true;
    setBusy(true);
    setError('');
    // Remove short-lived codes from the browser address/history immediately.
    if (!native) window.history.replaceState(null, '', '/login');
    try {
      const pending = JSON.parse(storage().getItem(STORAGE_KEY) || 'null');
      if (!pending || pending.state !== params.get('oauth_state') || pending.expiresAt < Date.now()) throw new Error('ログインの確認ができませんでした。同じ画面からやり直してください。');
      if (params.has('oauth_error')) throw new Error(params.get('oauth_error') === 'cancelled' ? 'ログインをキャンセルしました。' : '外部サービスでのログインに失敗しました。もう一度お試しください。');
      await completeSocialLogin(params.get('oauth_code'), pending.verifier, pending.state);
      storage().removeItem(STORAGE_KEY);
    } catch (err) {
      if (err.response?.data?.code === 'LINK_PASSWORD_REQUIRED') {
        const pending = JSON.parse(storage().getItem(STORAGE_KEY) || 'null');
        setLinkRequest({ ...pending, code: params.get('oauth_code'), provider: err.response.data.provider });
        setError('');
      } else {
        storage().removeItem(STORAGE_KEY);
        setError(err.response?.data?.error || err.message || 'ログインに失敗しました。');
      }
    } finally {
      if (native) await Browser.close().catch(() => {});
      handling.current = false;
      setBusy(false);
    }
  }, [completeSocialLogin]);

  useEffect(() => {
    if (!native) { receive(window.location.href); return undefined; }
    let disposed = false;
    const handles = [];
    const listen = async () => {
      const opened = await App.addListener('appUrlOpen', ({ url }) => receive(url));
      if (disposed) { await opened.remove(); return; }
      handles.push(opened);
      const closed = await Browser.addListener('browserFinished', () => setBusy(false));
      if (disposed) { await closed.remove(); return; }
      handles.push(closed);
      const launch = await App.getLaunchUrl();
      if (!disposed && launch?.url) receive(launch.url);
    };
    listen().catch(() => setError('ログイン画面を開けませんでした。アプリを再起動してください。'));
    return () => { disposed = true; handles.forEach(handle => handle.remove()); };
  }, [receive]);

  const start = async (provider, acceptedTerms) => {
    setError('');
    setLinkRequest(null);
    if (starting.current || busy) return;
    if (!providersLoaded) { setError('ログイン方法を確認しています。少し待ってからお試しください。'); return; }
    if (!providers.find(item => item.id === provider)?.enabled) {
      const name = providers.find(item => item.id === provider)?.name || 'このサービス';
      setError(`${name}でのログインは準備中です。メールをご利用ください。`);
      return;
    }
    if (!acceptedTerms) { setError('利用規約とプライバシーポリシーに同意してから、ログイン方法を選んでください。'); return; }
    starting.current = true;
    setBusy(true);
    try {
      const verifier = base64url(crypto.getRandomValues(new Uint8Array(32)));
      const challenge = base64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
      const { data } = await axios.post(`/api/auth/oauth/${provider}/start`, { accepted_terms: true, code_challenge: challenge, platform: native ? 'native' : 'web' }, { __retried: true });
      storage().setItem(STORAGE_KEY, JSON.stringify({ verifier, state: data.state, expiresAt: Date.now() + 600000 }));
      if (native) await Browser.open({ url: data.url });
      else window.location.assign(data.url);
    } catch (err) {
      storage().removeItem(STORAGE_KEY);
      setError(err.response?.data?.error || 'ログインを開始できませんでした。もう一度お試しください。');
      setBusy(false);
    } finally { starting.current = false; }
  };
  const cancelLink = () => { setLinkRequest(null); storage().removeItem(STORAGE_KEY); setError(''); };
  const confirmLink = async password => {
    if (starting.current || !linkRequest) return;
    starting.current = true;
    setBusy(true);
    setError('');
    try {
      if (linkRequest.expiresAt < Date.now()) throw new Error('確認の期限が切れました。Googleなどのログインボタンからやり直してください。');
      await completeSocialLogin(linkRequest.code, linkRequest.verifier, linkRequest.state, { password });
      setLinkRequest(null);
      storage().removeItem(STORAGE_KEY);
    } catch (err) {
      if (err.response?.data?.code !== 'LINK_PASSWORD_REQUIRED') {
        setLinkRequest(null);
        storage().removeItem(STORAGE_KEY);
      }
      setError(err.response?.data?.error || err.message || '連携に失敗しました。');
    } finally { starting.current = false; setBusy(false); }
  };
  return { providers, providersLoaded, busy, error, setError, start, linkRequest, confirmLink, cancelLink };
}
